import { useState, useEffect } from 'react';
import { txApi } from '../txApi.js';
import { C, inp, btn, label, SectionLabel, ListItem, Err, Spinner } from './_shared.jsx';
import PlatformDisableDialog from './_PlatformDisableDialog.jsx';

// Country is absent because the concept is gone from this module: nothing here ever read it,
// and the Countries tab that maintained the list has been removed. A client is per-country by
// definition anyway — the country is carried in the client's own name.
//
// Status is absent for a stronger reason: it is the platform-wide client switch, and turning it
// off here would hide the client from every other tool as well. It lives in Client Management,
// which is also where this client is checked in or out of Taxonomy Generator specifically. The
// backend ignores the key now, so sending it would silently do nothing.
function ClientDetail({ client, platforms, onUpdate }) {
  const [name,       setName]       = useState(client.name);
  const [platformIds,setPlatformIds]= useState(client.platform_ids || []);
  const [saving,     setSaving]     = useState(false);
  const [err,        setErr]        = useState(null);
  const [saved,      setSaved]      = useState(false);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    setName(client.name);
    setPlatformIds(client.platform_ids || []);
    setErr(null); setSaved(false); setConfirming(false);
  }, [client.id]);

  const togglePid = (pid) => setPlatformIds(prev => prev.includes(pid) ? prev.filter(x=>x!==pid) : [...prev, pid]);

  // Removing a platform here takes it away from this client only, and every campaign built on
  // it goes read-only. That is worth stating — but on Save, not on the chip: a chip is
  // reversible until saved, so warning per click fires for changes the user then undoes.
  const removed = (client.platform_ids || []).filter(pid => !platformIds.includes(pid));
  const removedPlatforms = removed
    .map(pid => platforms.find(p => String(p.id) === String(pid)))
    .filter(Boolean);

  const commit = async () => {
    setSaving(true); setErr(null);
    try {
      // The whole array goes, additions included — one PATCH, whether or not it was confirmed.
      await txApi.updateClient(client.id, { name: name.trim(), platform_ids: platformIds });
      setConfirming(false);
      setSaved(true); setTimeout(() => setSaved(false), 2000);
      onUpdate();
    } catch (e) { setConfirming(false); setErr(e.message); }
    finally { setSaving(false); }
  };

  const save = () => {
    if (removedPlatforms.length) { setConfirming(true); return; }
    commit();
  };

  const activePlatforms = platforms.filter(p => p.is_active);

  return (
    <div style={{ padding: '0 0 0 24px', flex: 1, overflowY: 'auto' }}>
      {/* Client name as big editable input */}
      <input
        value={name}
        onChange={e => setName(e.target.value)}
        placeholder="Client Name"
        style={{ ...inp(), fontSize: 20, fontWeight: 600, background: 'transparent', border: 'none', borderBottom: `1px solid ${C.border}`, borderRadius: 0, padding: '4px 0', marginBottom: 24, outline: 'none', color: C.text }}
      />

      <div style={{ marginBottom: 24 }}>
        <div style={{ ...label(), marginBottom: 10 }}>Platforms</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {activePlatforms.map(p => {
            const on = platformIds.includes(p.id);
            return (
              <button
                key={p.id}
                onClick={() => togglePid(p.id)}
                style={{
                  padding: '5px 14px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit',
                  fontSize: 13, fontWeight: on ? 700 : 400,
                  background: on ? C.ink : C.surface,
                  color: on ? C.bg : C.dim,
                  border: `1px solid ${on ? C.ink : C.border}`,
                }}
              >
                {p.name}
              </button>
            );
          })}
          {activePlatforms.length === 0 && <span style={{ color:C.muted, fontSize:12 }}>No active platforms. Add them in Platforms.</span>}
        </div>
      </div>

      <Err msg={err} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button style={btn('primary', { padding: '8px 20px', fontSize: 13 })} onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save Changes'}
        </button>
        {saved && <span style={{ color: C.success, fontSize: 12 }}>Saved</span>}
      </div>

      {confirming && (
        <PlatformDisableDialog
          platforms={removedPlatforms}
          clientId={client.id}
          clientName={client.name}
          onCancel={() => setConfirming(false)}
          onConfirm={commit}
        />
      )}
    </div>
  );
}

// Admin-only client register: one row per client, with the platforms it uses HERE. Clients are
// created and switched on or off in Client Management — this page administers the ones that
// reach this tool, and the rest of the module works in whichever one the workspace selector has
// active.
export default function ClientsPage() {
  const [clients,   setClients]   = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [err,       setErr]       = useState(null);
  const [selId,     setSelId]     = useState(null);

  const load = async () => {
    try {
      const [c, p] = await Promise.all([txApi.listClients(), txApi.listPlatforms()]);
      setClients(c.clients);
      setPlatforms(p.platforms);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const selected = clients.find(c => c.id === selId);

  if (loading) return <Spinner />;

  return (
    <div style={{ display: 'flex', gap: 0, height: 'calc(100vh - 140px)' }}>
      {/* Left sidebar */}
      <div style={{ width: 210, flexShrink: 0, borderRight: `1px solid ${C.border}`, paddingRight: 16, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
        <SectionLabel style={{ marginBottom: 10 }}>Clients</SectionLabel>
        <Err msg={err} />
        {!clients.length && <p style={{ fontSize: 12, color: C.muted, marginTop: 10 }}>No clients yet.</p>}
        {clients.map(c => (
          <ListItem key={c.id} active={selId === c.id} onClick={() => setSelId(c.id)}>
            {c.name || 'Unnamed'}
          </ListItem>
        ))}
      </div>

      {/* Right detail */}
      {selected
        ? <ClientDetail client={selected} platforms={platforms} onUpdate={load} />
        : <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', color:C.muted, fontSize:13 }}>
            ← Select a client to edit its platforms
          </div>
      }
    </div>
  );
}
